import { ROUTES } from '@/shared/config';
import BreadcrumbLink from '@/shared/ui/components/BreadcrumbLink';
import type { UIMatch } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { adminSectionRoutes } from './sections/administration';
import { careManagementRoutes } from './sections/care-management';
import { ccmSectionRoutes } from './sections/chronic-care-management';
import { patientSectionRoutes } from './sections/patients';
import { providerSectionRoutes } from './sections/providers';
import { rpmSectionRoutes } from './sections/remote-patient-monitoring';
import { reportsSectionRoutes } from './sections/reports';
import { requestsSectionRoutes } from './sections/requests';
import { taskSectionRoutes } from './sections/tasks';
import { tcmSectionRoutes } from './sections/transitional-care-management';
import type { RoutePathDefinition } from './types';

export const authenticatedRoutes: RoutePathDefinition[] = [
  {
    path: 'home',
    lazy: () => import('@/pages/home').then(m => ({ Component: m.default })),
    handle: {
      crumb: null
    }
  },
  {
    path: 'global-lookup',
    lazy: () => import('@/pages/global-lookup').then(m => ({ Component: m.default })),
    handle: {
      crumb: () => <BreadcrumbLink>Global Lookup</BreadcrumbLink>
    }
  },
  {
    path: 'notifications',
    lazy: () => import('@/pages/notifications').then(m => ({ Component: m.default })),
    handle: {
      crumb: () => <BreadcrumbLink>Notifications</BreadcrumbLink>
    }
  },
  {
    path: 'sitemap',
    lazy: () => import('@/pages/sitemap').then(m => ({ Component: m.default })),
    handle: {
      crumb: () => <BreadcrumbLink>Site map</BreadcrumbLink>
    }
  },
  {
    path: 'providers/:id',
    lazy: () => import('@/pages/providers/ui/index').then(m => ({ Component: m.default })),
    handle: {
      crumb: (data: UIMatch) => <BreadcrumbLink to={data.pathname + '/' + providerSectionRoutes[0].path}>{data.params.id}</BreadcrumbLink>
    },
    children: providerSectionRoutes
  },
  {
    path: 'patients',
    lazy: () => import('@/pages/patients').then(m => ({ Component: m.default })),
    handle: {
      crumb: (data: UIMatch) => {
        return <BreadcrumbLink to={data.pathname}>Patients</BreadcrumbLink>;
      }
    },
    children: patientSectionRoutes
  },
  {
    path: 'tasks',
    lazy: () => import('@/pages/tasks').then(m => ({ Component: m.default })),
    handle: {
      crumb: (data: UIMatch) => <BreadcrumbLink to={data.pathname + '/' + taskSectionRoutes[0].path}>Tasks</BreadcrumbLink>
    },
    children: taskSectionRoutes
  },
  {
    path: 'transitional-care-management/',
    lazy: () => import('@/pages/tcm').then(m => ({ Component: m.default })),
    handle: {
      crumb: (data: UIMatch) => <BreadcrumbLink to={data.pathname + '/' + tcmSectionRoutes[0].path}>TCM</BreadcrumbLink>
    },
    children: tcmSectionRoutes
  },
  {
    path: 'remote-patient-monitoring/',
    lazy: () => import('@/pages/remote-patient-monitoring').then(m => ({ Component: m.default })),
    handle: {
      crumb: (data: UIMatch) => <BreadcrumbLink to={data.pathname + '/' + rpmSectionRoutes[0].path}>Care Management</BreadcrumbLink>
    },
    children: rpmSectionRoutes
  },
  {
    path: 'chronic-care-management',
    lazy: () => import('@/pages/chronic-care-management').then(m => ({ Component: m.default })),
    handle: {
      crumb: (data: UIMatch) => <BreadcrumbLink to={data.pathname + '/' + rpmSectionRoutes[0].path}>Care Management</BreadcrumbLink>
    },
    children: ccmSectionRoutes
  },

  {
    path: 'care-management',
    lazy: () => import('@/pages/care-management').then(m => ({ Component: m.default })),
    handle: {
      crumb: (data: UIMatch) => <BreadcrumbLink to={data.pathname + '/' + careManagementRoutes[0].path}>Patients</BreadcrumbLink>
    },
    children: careManagementRoutes
  },

  {
    path: 'requests',
    lazy: () => import('@/pages/requests').then(m => ({ Component: m.default })),
    children: requestsSectionRoutes,
    handle: { crumb: (data: UIMatch) => <BreadcrumbLink to={data.pathname + '/' + requestsSectionRoutes[0].path}>Requests</BreadcrumbLink> }
  },
  {
    path: 'reports',
    lazy: () => import('@/pages/reports/ui/ReportsSection').then(m => ({ Component: m.default })),
    handle: {
      crumb: (data: UIMatch) => <BreadcrumbLink to={data.pathname + '/' + reportsSectionRoutes[0].path}>Reports</BreadcrumbLink>
    },
    children: reportsSectionRoutes
  },
  {
    path: 'administration',
    lazy: () => import('@/pages/administration').then(m => ({ Component: m.default })),
    handle: {
      crumb: (data: UIMatch) => <BreadcrumbLink to={data.pathname + '/' + adminSectionRoutes[0].path}>Administration</BreadcrumbLink>
    },
    children: adminSectionRoutes
  },
  { path: '*', Component: () => <Navigate to={ROUTES.ERROR.NOT_FOUND} replace /> }
];
